<script lang="ts">
  import * as d3 from 'd3';
  import type { ChartQuantitative } from '$lib/sanity/schemas';
  import ChartLayout from './components/ChartLayout.svelte';
  import Legend from './components/Legend.svelte';
  import Table from './components/Table.svelte';
  import {
    addAxisLabels,
    addBarSegements,
    addDataLabels,
    setUpChart,
    type AxisLabel,
    type Bar,
    type DataLabel
  } from './utils/chart';
  import { getPalette } from './utils/color';
  import { categorizeChartItems, formatDocumentId, formatGraphValue } from './utils/data';
  import {
    barInteraction,
    interactionStore,
    labelInteraction,
    labelBackgroundInteraction
  } from './utils/interactions';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import { sanityStudioURL } from '$lib/client';

  interface Props {
    document: ChartQuantitative;
    sourceMap: ContentSourceMap;
    path?: StudioPathLike;
    gridColspan: number;
  }

  let { document, sourceMap, path = [], gridColspan }: Props = $props();

  let encodeDataAttribute = $derived(useEncodeDataAttribute(document, sourceMap, sanityStudioURL));

  const {
    _id,
    alt,
    chart = null,
    colorTheme = '',
    displayAxisLabels,
    footnote = null,
    headline = '',
    legendDisplay = '',
    prefix = '',
    subhead = '',
    suffix = ''
  } = document;
  const { items = [] } = chart || {};
  const categories = categorizeChartItems(items);
  const id = formatDocumentId(_id);
  const store = interactionStore(id);
  const colorPalette = getPalette(colorTheme, categories.primary.length);
  const tableProps = {
    header: ['Category', 'Group', 'Value'],
    rows: items.map((item) => {
      return [item.primaryCategory, item.secondaryCategory, item.value];
    })
  };
  const groups = d3.groups(items, (d) => d.primaryCategory);
  const xAxisGap = displayAxisLabels ? 50 : 0;

  const renderChart = (chartElement: HTMLElement) => {
    const { selection, sizes } = setUpChart(chartElement, { alt, colorPalette, xAxisGap });
    const values = items.map((item) => {
      return item.value || 0;
    });
    const maxValue = d3.max(values) || 0;
    const groupScale = d3
      .scaleBand(categories.primary, [sizes.margin, sizes.chart.width])
      .paddingInner(0.1);
    const xScale = d3
      .scaleBand(categories.secondary, [0, groupScale.bandwidth()])
      .paddingInner(0.05);
    const yScale = d3.scaleLinear(
      [0, maxValue],
      // Use a percent to create room for the labels
      [0, sizes.graph.height * 0.9]
    );

    // Draw bars
    selection
      .selectAll('.bar-group')
      .data(groups)
      .enter()
      .append('g')
      .attr('transform', function (d) {
        return `translate(${groupScale(d[0])}, 0)`;
      })
      .each(function (d) {
        const groupSelection = d3.select(this);
        const [key, data] = d;

        const Bar: Bar[] = data.map(function (item): Bar {
          const colorKey = categories.primary.indexOf(key);

          return {
            color: colorPalette?.colors[colorKey],
            height: yScale(item.value),
            isGradient: colorPalette?.isGradient,
            width: xScale.bandwidth(),
            x: xScale(item.secondaryCategory) || 0,
            y: sizes.graph.height - yScale(item.value)
          };
        });

        addBarSegements(groupSelection, Bar, store);
      });

    // Draw data labels
    const dataLabels: DataLabel[] = items.map(function (item): DataLabel {
      const colorKey = categories.primary.indexOf(item.primaryCategory);
      const color = colorPalette?.colors[colorKey];
      const selected = color?.id ? [color?.id] : [];

      return {
        color: '#ffffff',
        selected,
        text: formatGraphValue(item.value, prefix, suffix),
        x:
          (groupScale(item.primaryCategory) || 0) +
          (xScale(item.secondaryCategory) || 0) +
          xScale.bandwidth() / 2,
        y: sizes.graph.height - yScale(item.value) - 30
      };
    });

    addDataLabels(selection, dataLabels, store);

    // Draw axis labels
    if (displayAxisLabels) {
      const axisLabels: AxisLabel[] = items.map((item) => ({
        text: item.secondaryCategory,
        x:
          (groupScale(item.primaryCategory) || 0) +
          (xScale(item.secondaryCategory) || 0) +
          xScale.bandwidth() / 2,
        y: sizes.chart.height - 20
      }));

      addAxisLabels(selection, axisLabels);
    }
  };
</script>

<ChartLayout
  {footnote}
  {headline}
  {id}
  {legendDisplay}
  {subhead}
  {renderChart}
  {path}
  {encodeDataAttribute}
  {gridColspan}
>
  {#snippet legend()}
    <Legend
      categories={categories.primary}
      {colorPalette}
      interactions={[barInteraction, labelInteraction, labelBackgroundInteraction]}
      {legendDisplay}
      {store}
      {gridColspan}
    />
  {/snippet}
  {#snippet table()}
    <Table {...tableProps} {gridColspan} />
  {/snippet}
</ChartLayout>
